/*
 * THE HEADER, READ ONCE: who this is and where we stand, nothing else (the brief,
 * "Who owns what"). The header and the slim sticky line both draw from here, so
 * the pill on the sticky line can never disagree with the pill it stands in for.
 *
 * Everything situational is already decided by the shell (record/situation.mjs:
 * the situation, positionLine, scoreProminence) and the score is already worded
 * (record/scoreView.mjs). This module only answers the header's own questions:
 *
 *   - which badge, if any: featured for new leads, a booked call and a parked
 *     company; small mid-deal; none for clients, churned and lost (the score
 *     moves to Fit, "Would score 33 today"); none when scoring is not set up,
 *     never a dash;
 *   - an associated colleague's badge is the COMPANY's score, so it follows the
 *     company's situation (no badge on a client's colleague either), is always
 *     small, and says whose facts it read ("scored on Shahar Bahat");
 *   - the Proposed sub-state slot (B-008), only where a sub-state means
 *     something: Agreement + legal and Pricing sent (reality.txt [25] hides it on
 *     clients, where the mockup drew it on Pluto).
 *
 * Pure, so test/record-header-model.test.js pins it without a browser.
 */
import { scoreProminence } from "./situation.mjs";
import { fullName } from "./format.mjs";

// B-008's sub-states ("Legal negotiation · waiting on Ella · review Oct 14") are
// defined for these two stages only. Nothing stores one yet, so the slot is the
// inert "Add sub-state" with its Proposed tag.
export const SUBSTATE_SITUATIONS = new Set(["agreement", "pricing_sent"]);

/** The situation the position line speaks for: an associated colleague's is the company's. */
export function positionSituation(view) {
  if (!view) return null;
  if (view.key === "associated") return (view.situation && view.situation.company_situation) || null;
  return view.key || null;
}

/**
 * "featured" | "small" | null: the badge the header draws.
 *
 * A blocked score is still drawn where a score belongs ("0 · blocked" is the
 * answer to "why did the auto-reply not answer them"), in the small shape
 * ScoreBadge gives every blocked badge.
 */
export function badgeSize(view, score) {
  if (!view || !score || !score.show) return null;
  if (view.key === "associated") {
    const theirs = scoreProminence(positionSituation(view));
    return theirs === "featured" || theirs === "small" ? "small" : null;
  }
  if (view.prominence === "featured") return "featured";
  if (view.prominence === "small") return "small";
  return null;
}

/**
 * The few words beside a badge that was not scored on this person: "scored on
 * Shahar Bahat". The breakdown carries the full sentence, with "(point of
 * contact)"; the header only has room to say the facts are someone else's.
 */
export function scoredOnNote(score, leadScore) {
  if (!score || !score.show || !score.scoredOn) return null;
  const name = leadScore && leadScore.contact_name ? String(leadScore.contact_name).trim() : "";
  return name ? `scored on ${name}` : null;
}

/** The pill's class names: the variant's treatment, and the hatch for anything on hold. */
export function pillClass(position, { small = false } = {}) {
  const p = position || {};
  const variant = p.variant || "outline";
  const hatch = p.hatch && variant !== "hatch" ? " rec-pos--hatch" : "";
  return `rec-pos${small ? " rec-pos--sm" : ""} rec-pos--${variant}${hatch}`;
}

/**
 * Everything RecordHeader and StickyLine render.
 *
 *   name       "Shahar Bahat" (the address when there is no name, never blank)
 *   title      "Co-Founder & CEO" | null
 *   company    "Pluto Security" | null
 *   source     sourcePill() as the shell computed it, or null (never guessed)
 *   pill       { text, className } | null, the position pill that opens Change status…
 *   text       "Client since Sep 30 · day 1" | null, the words beside the pill
 *   subState   true where the Proposed sub-state slot belongs
 *   badge      "featured" | "small" | null
 *   scoredOn   "scored on Shahar Bahat" | null
 *   hasLine    whether the position line has anything on it at all
 */
export function headerView({ contact = null, company = null, view = null, score = null, source = null } = {}) {
  const p = (view && view.position) || {};
  const situation = positionSituation(view);
  const badge = badgeSize(view, score);
  const pill = p.pill ? { text: p.pill, className: pillClass(p), smallClassName: pillClass(p, { small: true }) } : null;
  const subState = SUBSTATE_SITUATIONS.has(situation);
  const scoredOn = badge ? scoredOnNote(score, company && company.lead_score) : null;
  return {
    name: fullName(contact) || "(no name)",
    title: contact && contact.job_title ? String(contact.job_title).trim() || null : null,
    company: company && company.name ? company.name : null,
    source: source || null,
    pill,
    text: p.text || null,
    subState,
    badge,
    scoredOn,
    hasLine: !!(pill || p.text || badge || subState),
  };
}
